'use client'

import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import * as Popover from '@radix-ui/react-popover'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ComponentProps } from 'react'
import { cn } from '@chaseweb/utils/cn'
import { CustomSvg } from '../../atoms/svg'

const RootContext = createContext<{
  closestBoundaryContainer: Element | null
}>({ closestBoundaryContainer: null })

const Root = ({
  children,
  modal = true,
  ...props
}: ComponentProps<typeof Popover.Root>) => {
  // const containerRef = useRef<HTMLElement>(null)
  const boundaryElRef = useRef<HTMLDivElement>(null)
  const [closestBoundaryContainer, setClosestBoundaryContainer] =
    useState<Element | null>(null)

  useEffect(() => {
    if (boundaryElRef.current) {
      setClosestBoundaryContainer(boundaryElRef.current)
    }
  }, [boundaryElRef.current])

  const contextValues = useMemo(
    () => ({
      closestBoundaryContainer,
    }),
    [closestBoundaryContainer],
  )

  return (
    <Popover.Root modal={modal} {...props}>
      <span
        className="popover-boundary tw-pointer-events-none tw-absolute -tw-top-[100vh] tw-left-0 -tw-z-50 tw-h-[200vh] tw-w-full"
        ref={boundaryElRef}
      />
      <RootContext.Provider value={contextValues}>
        {children}
      </RootContext.Provider>
    </Popover.Root>
  )
}
Root.displayName = 'Popover.Root'

const triggerVariants = cva(
  'tw-h-6 tw-cursor-pointer focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-focusRing',
  {
    variants: {
      variant: {
        primary: 'tw-text-link',
        secondary: 'tw-text-white',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
)
const Trigger = ({
  children,
  variant,
  className,
  ...props
}: ComponentProps<typeof Popover.Trigger> &
  VariantProps<typeof triggerVariants>) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const lastResizeTime = useRef<Date>(null)
  const [isMultiLineAnchor, setIsMultiLineAnchor] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (triggerRef.current) {
        const triggerEl = triggerRef.current
        if (
          !lastResizeTime.current ||
          Date.now() - lastResizeTime.current.getTime() > 200
        ) {
          const triggerHeight = triggerEl.getBoundingClientRect().height
          const triggerLineHeight = parseInt(
            window.getComputedStyle(triggerEl).lineHeight,
            10,
          )
          if (!isMultiLineAnchor && triggerHeight > triggerLineHeight) {
            setIsMultiLineAnchor(true)
          } else if (isMultiLineAnchor && triggerHeight < triggerLineHeight) {
            setIsMultiLineAnchor(false)
          }
          lastResizeTime.current = new Date()
        }
      }
    }
    window.addEventListener('resize', handleResize, false)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize, false)
    }
  }, [triggerRef.current, isMultiLineAnchor])

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    ev.stopPropagation()
    ev.preventDefault()
    if (['Enter', ' '].includes(ev.key)) {
      ;(ev.currentTarget as HTMLButtonElement).click()
    }
  }

  return (
    <Popover.Trigger
      className={cn(triggerVariants({ variant }), className)}
      {...props}
      asChild
    >
      <span
        tabIndex={0}
        role="button"
        ref={triggerRef}
        onKeyDown={handleKeyDown}
      >
        {isMultiLineAnchor ? (
          <span className="tw-relative tw-border-b tw-border-dashed tw-leading-[1.1em]">
            <Popover.Anchor asChild>
              <span
                className="tw-absolute tw-left-8"
                data-testid="popover-anchor"
              ></span>
            </Popover.Anchor>
            {children}
          </span>
        ) : (
          <Popover.Anchor asChild>
            <span className="tw-border-b tw-border-dashed tw-leading-[1.1em]">
              {children}
            </span>
          </Popover.Anchor>
        )}
        <CustomSvg
          className="tw-relative tw-ml-[3.5px] tw-inline tw-w-4"
          name="Information"
        />
      </span>
    </Popover.Trigger>
  )
}
Trigger.displayName = 'Popover.Trigger'

const Content = ({
  children,
  className,
  ...props
}: ComponentProps<typeof Popover.Content>) => {
  const { closestBoundaryContainer } = useContext(RootContext)
  return (
    <Popover.Portal>
      <Popover.Content
        collisionBoundary={closestBoundaryContainer}
        arrowPadding={-20}
        className={cn(
          'tw-flex tw-max-w-[var(--radix-popover-content-available-width)] tw-items-center tw-rounded-2xl tw-border tw-border-blue70 tw-bg-blue90 tw-p-6 tw-text-sm tw-text-grey10 tw-shadow-popover after:tw-left-8',
          className,
        )}
        {...props}
      >
        {children}
      </Popover.Content>
    </Popover.Portal>
  )
}
Content.displayName = 'Popover.Content'

const Close = ({
  children,
  className,
  ...props
}: ComponentProps<typeof Popover.Close>) => {
  return (
    <Popover.Close className={cn('tw-ml-1', className)} {...props}>
      <CustomSvg name="Close" />
    </Popover.Close>
  )
}
Close.displayName = 'Popover.Close'

const Arrow = ({
  children,
  className,
  ...props
}: ComponentProps<typeof Popover.Arrow>) => {
  return (
    <Popover.Arrow asChild {...props}>
      <span className="-tw-mt-2 tw-h-4 tw-w-4 tw-rotate-45 tw-border tw-border-l-0 tw-border-t-0 tw-border-blue70 tw-bg-blue90"></span>
    </Popover.Arrow>
  )
}
Arrow.displayName = 'Popover.Arrow'

export { Root, Trigger, Content, Close, Arrow }
